import React from 'react';
import { X, BookOpen, User, Calendar, MapPin, Globe, FileText, Tag, Hash } from 'lucide-react';
import { GoogleSheetsBook } from '@/services/libraryClient';

interface BookDetailModalProps {
  book: GoogleSheetsBook;
  isOpen: boolean;
  onClose: () => void;
}

const BookDetailModal: React.FC<BookDetailModalProps> = ({ book, isOpen, onClose }) => {
  if (!isOpen) return null;

  // Handle background click
  const handleBackgroundClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={handleBackgroundClick}
    >
      <div 
        className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-green-900 to-green-700 text-white p-6 rounded-t-lg flex justify-between items-start">
          <div className="flex-1 pr-8">
            <h2 className="text-2xl font-bold mb-2">{book.title || 'Untitled'}</h2>
            {book.author && (
              <p className="text-green-100 text-lg flex items-center gap-2">
                <User className="w-5 h-5" />
                {book.author}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-green-200 transition-colors flex-shrink-0"
            aria-label="Close modal"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex flex-col md:flex-row gap-8">
            {/* Left Column - Cover Image */}
            <div className="md:w-1/3 flex-shrink-0">
              {book.coverUrl ? (
                <img
                  src={book.coverUrl}
                  alt={`Cover of ${book.title}`}
                  className="w-full rounded-lg shadow-lg"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : (
                <div className="w-full aspect-[2/3] bg-gradient-to-br from-green-100 to-green-50 rounded-lg shadow-lg flex items-center justify-center">
                  <BookOpen className="w-24 h-24 text-green-600 opacity-40" />
                </div>
              )}

              {/* Quick Reference Info */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-bold text-gray-900 mb-3">Library Information</h3>
                <div className="space-y-2 text-sm">
                  {book.referenceNo && (
                    <div>
                      <span className="font-semibold text-gray-700">Reference No:</span>
                      <p className="text-gray-600 font-mono">{book.referenceNo}</p>
                    </div>
                  )}
                  {book.copies && (
                    <div>
                      <span className="font-semibold text-gray-700">Copies Available:</span>
                      <p className="text-gray-600">{book.copies}</p>
                    </div>
                  )}
                  {book.category && (
                    <div>
                      <span className="font-semibold text-gray-700">Category:</span>
                      <p className="text-gray-600">{book.category}</p>
                    </div>
                  )}
                  {book.genre && (
                    <div>
                      <span className="font-semibold text-gray-700">Genre:</span>
                      <p className="text-gray-600">{book.genre}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - Detailed Information */}
            <div className="md:w-2/3 space-y-6">
              
              {/* Description */}
              {book.description && (
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-green-600" />
                    Description
                  </h3>
                  <p className="text-gray-700 leading-relaxed">{book.description}</p>
                </div>
              )}

              {/* Publication Information */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-green-600" />
                  Publication Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {book.publisher && (
                    <div className="flex items-start gap-2">
                      <Globe className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-gray-700">Publisher</p>
                        <p className="text-gray-600">{book.publisher}</p>
                      </div>
                    </div>
                  )}
                  {book.publicationYear && (
                    <div className="flex items-start gap-2">
                      <Calendar className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-gray-700">Publication Year</p>
                        <p className="text-gray-600">{book.publicationYear}</p>
                      </div>
                    </div>
                  )}
                  {book.publicationPlace && (
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-gray-700">Publication Place</p>
                        <p className="text-gray-600">{book.publicationPlace}</p>
                      </div>
                    </div>
                  )}
                  {book.language && (
                    <div className="flex items-start gap-2">
                      <Globe className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-gray-700">Language</p>
                        <p className="text-gray-600">{book.language.toUpperCase()}</p>
                      </div>
                    </div>
                  )}
                  {book.editionCount && book.editionCount > 0 && (
                    <div className="flex items-start gap-2">
                      <Hash className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-gray-700">Editions</p>
                        <p className="text-gray-600">{book.editionCount} edition{book.editionCount > 1 ? 's' : ''}</p>
                      </div>
                    </div>
                  )}
                  {book.numberOfPages && (
                    <div className="flex items-start gap-2">
                      <FileText className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-gray-700">Pages</p>
                        <p className="text-gray-600">{book.numberOfPages} pages</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Subjects/Topics */}
              {book.subjects && book.subjects.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Tag className="w-5 h-5 text-green-600" />
                    Subjects
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {book.subjects.map((subject, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium"
                      >
                        {subject}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Identifiers */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Hash className="w-5 h-5 text-green-600" />
                  Identifiers
                </h3>
                <div className="space-y-2">
                  {book.isbn13 && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-semibold text-gray-700 w-24">ISBN-13:</span>
                      <span className="text-gray-600 font-mono">{book.isbn13}</span>
                    </div>
                  )}
                  {book.isbn10 && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-semibold text-gray-700 w-24">ISBN-10:</span>
                      <span className="text-gray-600 font-mono">{book.isbn10}</span>
                    </div>
                  )}
                  {!book.isbn13 && !book.isbn10 && book.isbn && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-semibold text-gray-700 w-24">ISBN:</span>
                      <span className="text-gray-600 font-mono">{book.isbn}</span>
                    </div>
                  )}
                  {book.oclc && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-semibold text-gray-700 w-24">OCLC:</span>
                      <span className="text-gray-600 font-mono">{book.oclc}</span>
                    </div>
                  )}
                  {book.lccn && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-semibold text-gray-700 w-24">LCCN:</span>
                      <span className="text-gray-600 font-mono">{book.lccn}</span>
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-lg border-t border-gray-200">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              For access to this publication, please visit the Highland Institute Library
            </p>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookDetailModal;
